import React, { useCallback, useEffect, useRef, useState } from "react";
import { GetInventoryGroupedByDescription, PowderInventoryGroupedByDescription } from "../../../services/PowderFormService";
import { useToast } from "../../ToastProvider";
import CustomDropdown from "../CustomDropdown";
import { useRawMaterialControl } from "../redux/ReduxContext";
import { RawMaterial } from "../redux/rawMaterialControl";
import { RawMaterialOrderRow } from "./RawMaterialOrderRow";

const DropdownChevron = () => (
    <svg
        className="w-4 h-4 text-primary-dark dark:text-primary-light"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        viewBox="0 0 24 24"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
        />
    </svg>
);

const tableHeaderText = "text-xs text-primary-dark dark:text-primary-light truncate text-left";
const lotColumnWidthClass = "w-[96px] min-w-[96px] max-w-[96px] sm:w-[168px] sm:min-w-[168px] sm:max-w-[168px]";

export default function RawMaterialOrder() {
    const { state, dispatch } = useRawMaterialControl();
    const { showToast } = useToast();
    const [inventory, setInventory] = useState<PowderInventoryGroupedByDescription[]>([]);
    const [selectedMaterial, setSelectedMaterial] = useState("");
    const [selectedLot, setSelectedLot] = useState("");
    const [isHeaderLotDropdownOpen, setIsHeaderLotDropdownOpen] = useState(false);
    const elementsScrollRef = useRef<HTMLDivElement | null>(null);
    const elementsScrollbarTrackRef = useRef<HTMLDivElement | null>(null);
    const elementsDragStateRef = useRef<{ startX: number; startThumbLeft: number } | null>(null);
    const headerLotTriggerRef = useRef<HTMLButtonElement | null>(null);
    const [elementsThumbWidth, setElementsThumbWidth] = useState(0);
    const [elementsThumbLeft, setElementsThumbLeft] = useState(0);
    const [isElementsThumbDragging, setIsElementsThumbDragging] = useState(false);

    const selectedMaterialLots = inventory.find((item) => item.Description === selectedMaterial)?.Lots ?? [];
    const textClass = "text-primary-dark dark:text-primary-light";

    useEffect(() => {
        (async () => {
            const response = await GetInventoryGroupedByDescription();
            if (!response) {
                setInventory([]);
                return;
            }

            setInventory(
                response.map((item) => ({
                    ...item,
                    Lots: item.Lots.filter((lot) => lot.Lot && lot.Lot.trim() !== ""),
                }))
            );
        })();
    }, []);

    const addMaterial = (materialDescription: string, lotValue: string) => {
        if (!materialDescription || !lotValue) {
            return;
        }

        if (state.materials.some((material) => material.raw_mtrl === materialDescription && material.lot === lotValue)) {
            showToast({
                title: "Material already added",
                intent: "warning",
                body: `The material ${materialDescription} with lot ${lotValue} has already been added.`,
            });
            return;
        }

        const lot = inventory
            .find((item) => item.Description === materialDescription)
            ?.Lots.find((lotItem) => lotItem.Lot === lotValue);

        if (!lot) {
            showToast({
                title: "Lot not found",
                intent: "error",
                body: `Could not find inventory data for ${materialDescription} lot ${lotValue}.`,
            });
            return;
        }

        const rawMaterial: RawMaterial = {
            raw_mtrl: materialDescription,
            lot: lotValue,
            rvid: lot.Rvid ?? "",
            role: "main",
            inStockGrams: lot.Qty ?? 0,
            weightG: 0,
        };

        dispatch({ type: "ADD_ARRAY_ITEM_LAST", payload: { field: "materials", item: rawMaterial } });
        setSelectedMaterial("");
        setSelectedLot("");
    };

    const updateMaterialLot = (index: number, nextLot: string) => {
        const material = state.materials[index];

        if (!material?.raw_mtrl || !nextLot || material.lot === nextLot) {
            return;
        }

        if (state.materials.some((item, itemIndex) => itemIndex !== index && item.raw_mtrl === material.raw_mtrl && item.lot === nextLot)) {
            showToast({
                title: "Material already added",
                intent: "warning",
                body: `The material ${material.raw_mtrl} with lot ${nextLot} has already been added.`,
            });
            return;
        }

        const nextLotItem = inventory
            .find((item) => item.Description === material.raw_mtrl)
            ?.Lots.find((lotItem) => lotItem.Lot === nextLot);

        if (!nextLotItem) {
            showToast({
                title: "Lot not found",
                intent: "error",
                body: `Could not find inventory data for ${material.raw_mtrl} lot ${nextLot}.`,
            });
            return;
        }

        dispatch({
            type: "UPDATE_ARRAY_ITEM",
            payload: {
                field: "materials",
                index,
                value: (prevItem: RawMaterial) => ({
                    ...prevItem,
                    lot: nextLot,
                    rvid: nextLotItem.Rvid ?? "",
                    inStockGrams: nextLotItem.Qty ?? 0,
                    powderComposition: null,
                    oPercent: undefined,
                    cPercent: undefined,
                }),
            },
        });
    };

    const updateScrollbar = useCallback(() => {
        const scrollEl = elementsScrollRef.current;
        const trackEl = elementsScrollbarTrackRef.current;
        if (!scrollEl || !trackEl) return;
        const { scrollWidth, clientWidth, scrollLeft } = scrollEl;
        const trackWidth = trackEl.clientWidth;
        if (scrollWidth <= clientWidth) {
            setElementsThumbWidth(0);
            setElementsThumbLeft(0);
            return;
        }
        const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 32);
        const maxThumbLeft = trackWidth - thumbWidth;
        const thumbLeft = (scrollLeft / (scrollWidth - clientWidth)) * maxThumbLeft;
        setElementsThumbWidth(thumbWidth);
        setElementsThumbLeft(thumbLeft);
    }, []);

    useEffect(() => {
        const scrollEl = elementsScrollRef.current;
        if (!scrollEl) return;
        updateScrollbar();
        const handleScroll = () => updateScrollbar();
        scrollEl.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', updateScrollbar);
        return () => {
            scrollEl.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', updateScrollbar);
        };
    }, [updateScrollbar]);

    useEffect(() => {
        updateScrollbar();
    });

    useEffect(() => {
        if (!isElementsThumbDragging) return;
        const scrollEl = elementsScrollRef.current;
        const trackEl = elementsScrollbarTrackRef.current;
        if (!scrollEl || !trackEl) return;
        const trackWidth = trackEl.clientWidth;
        const thumbWidth = elementsThumbWidth;
        const maxThumbLeft = trackWidth - thumbWidth;
        const maxScrollLeft = scrollEl.scrollWidth - scrollEl.clientWidth;
        const handlePointerMove = (e: PointerEvent) => {
            if (!elementsDragStateRef.current) return;
            const { startX, startThumbLeft } = elementsDragStateRef.current;
            const dx = e.clientX - startX;
            let newThumbLeft = Math.min(Math.max(startThumbLeft + dx, 0), maxThumbLeft);
            let newScrollLeft = (newThumbLeft / maxThumbLeft) * maxScrollLeft;
            if (isNaN(newScrollLeft)) newScrollLeft = 0;
            scrollEl.scrollLeft = newScrollLeft;
        };
        const handlePointerUp = () => {
            setIsElementsThumbDragging(false);
            elementsDragStateRef.current = null;
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };
    }, [isElementsThumbDragging, elementsThumbWidth]);

    return (
        <div className="bg-white dark:bg-oi-dark-background p-4 mb-4 drop-shadow-md">
            <div className="mb-2 flex items-center justify-between gap-3">
                <div className={`font-bold text-sm ${textClass}`}>Raw material order</div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[auto_minmax(0,1fr)] items-start gap-0">
                <div ref={elementsScrollRef} className="relative w-full max-w-full pb-2 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden  flex flex-row">
                    <div className="flex flex-col w-full">
                        <div id="elements-scroll-area" className="flex overflow-x-scroll scroll-smooth">

                            <table className="table-fixed whitespace-nowrap border-separate border-spacing-0 text-left w-full">
                                <thead>
                                    <tr className="flex flex-row">
                                        <th className="w-8 flex-none">
                                            <div className="h-[48px] w-7 items-center justify-center flex" />
                                        </th>
                                        <th className="w-[104px] min-w-[104px] sm:w-[180px] sm:min-w-[180px] flex-col flex-none">
                                            <div className={tableHeaderText}>Raw material</div>
                                            <input
                                                type="text"
                                                list="raw-material-order-options"
                                                placeholder="Search raw material"
                                                value={selectedMaterial}
                                                onChange={(e) => {
                                                    setSelectedMaterial(e.currentTarget.value);
                                                    setSelectedLot("");
                                                }}
                                                onFocus={() => setSelectedMaterial("")}
                                                className={`font-normal outline-none w-full h-[48px] bg-primary-light dark:bg-primary-dark px-2 text-xs ${textClass}`}
                                            />
                                            <datalist id="raw-material-order-options">
                                                {inventory.map((item, index) => (
                                                    <option key={index} value={item.Description} />
                                                ))}
                                            </datalist>
                                        </th>
                                        <th className={`${lotColumnWidthClass} overflow-hidden flex flex-col flex-none`}>
                                            <div className={tableHeaderText}>Lot</div>
                                            <button
                                                ref={headerLotTriggerRef}
                                                type="button"
                                                className="relative flex h-12 w-full items-center bg-primary-light pl-4 pr-10 text-left text-xs text-primary-dark dark:bg-primary-dark dark:text-primary-light"
                                                onClick={() => setIsHeaderLotDropdownOpen(!isHeaderLotDropdownOpen)}
                                            >
                                                <span className="truncate">{selectedLot || "Select lot"}</span>
                                                <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2">
                                                    <DropdownChevron />
                                                </span>
                                            </button>
                                            <CustomDropdown
                                                value={selectedLot}
                                                onChange={(nextLot) => {
                                                    setSelectedLot(nextLot);
                                                    addMaterial(selectedMaterial, nextLot);
                                                }}
                                                options={selectedMaterialLots.map((lot) => ({
                                                    label: lot.Lot,
                                                    value: lot.Lot,
                                                    HasComposition: lot.HasComposition,
                                                }))}
                                                open={isHeaderLotDropdownOpen}
                                                onOpenChange={setIsHeaderLotDropdownOpen}
                                                targetRef={headerLotTriggerRef}
                                            />
                                        </th>
                                        {state.materials.length > 0 && (
                                            <>
                                                <th className="flex-1 min-w-[60px]">
                                                    <div className={tableHeaderText + " text-center"}>C%</div>
                                                </th>
                                                <th className="flex-1 min-w-[60px]">
                                                    <div className={tableHeaderText + " text-center"}>O%</div>
                                                </th>
                                                <th className="flex-1 min-w-[100px]">
                                                    <div className={tableHeaderText + " text-center"}>Weight (g)</div>
                                                </th>
                                                <th className="flex-1 min-w-[120px]">
                                                    <div className={tableHeaderText + " text-center"}>Comment</div>
                                                </th>
                                                <th className="flex-1 min-w-[100px]">
                                                    <div className={tableHeaderText + " text-center"}>Stock (g)</div>
                                                </th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {state.materials.map((row, index) => (
                                        <RawMaterialOrderRow
                                            key={`${row.raw_mtrl}-${row.lot}-${index}`}
                                            index={index}
                                            inventory={inventory}
                                            onChangeLot={updateMaterialLot}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            <div className="w-full mt-1">
                <div
                    ref={elementsScrollbarTrackRef}
                    className="relative h-3 rounded bg-gray-200 dark:bg-gray-700"
                    style={{ userSelect: 'none', height: '12px' }}
                    onPointerDown={(e) => {
                        const scrollEl = elementsScrollRef.current;
                        const trackEl = elementsScrollbarTrackRef.current;
                        if (!scrollEl || !trackEl) return;
                        const rect = trackEl.getBoundingClientRect();
                        const clickX = e.clientX - rect.left;
                        const trackWidth = trackEl.clientWidth;
                        const thumbWidth = elementsThumbWidth;
                        const maxThumbLeft = trackWidth - thumbWidth;
                        const maxScrollLeft = scrollEl.scrollWidth - scrollEl.clientWidth;
                        if (maxScrollLeft <= 0 || maxThumbLeft <= 0) return;
                        const nextThumbLeft = Math.max(0, Math.min(clickX - (thumbWidth / 2), maxThumbLeft));
                        scrollEl.scrollLeft = (nextThumbLeft / maxThumbLeft) * maxScrollLeft;
                    }}
                >
                    {elementsThumbWidth > 0 && (
                        <div
                            role="scrollbar"
                            aria-label="Horizontal scroll handle"
                            aria-controls="elements-scroll-area"
                            aria-valuenow={Math.round((elementsThumbLeft / Math.max(1, (elementsScrollbarTrackRef.current?.clientWidth || 1) - elementsThumbWidth)) * 100)}
                            tabIndex={0}
                            className="absolute top-0 h-3 rounded bg-oi-button-blue/90 dark:bg-oi-powder-form-yellow/90 cursor-grab active:cursor-grabbing"
                            style={{ width: `${elementsThumbWidth}px`, transform: `translateX(${elementsThumbLeft}px)`, height: '12px' }}
                            onPointerDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                elementsDragStateRef.current = {
                                    startX: e.clientX,
                                    startThumbLeft: elementsThumbLeft,
                                };
                                setIsElementsThumbDragging(true);
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
